import Company from '../interfaces/Company';
import { Subscription } from '../interfaces/Subscription';

export type V2PlanTab = 'call' | 'chat' | 'bundle';

export function isEnterpriseSubscriber(
  company: Company | null | undefined,
  subscription: Subscription | null | undefined
): boolean {
  if (!company && !subscription) return false;
  if (company?.enterprise === true) return true;
  if (company?.plan_tier === 'enterprise') return true;
  if (company?.plan_id?.toLowerCase().includes('enterprise')) return true;
  const productName = subscription?.product_name?.toLowerCase() ?? '';
  return productName.includes('enterprise');
}

export function shouldShowUpgradeCta(
  company: Company | null | undefined,
  subscription: Subscription | null | undefined
): boolean {
  if (isEnterpriseSubscriber(company, subscription)) return false;
  return !subscription;
}

/** Paid subscriber: active-like Stripe subscription and non-free paid tier on company. */
export function isPaidPlanSubscriber(
  company: Company | null | undefined,
  subscription: Subscription | null | undefined
): boolean {
  if (!company) return false;
  if (isEnterpriseSubscriber(company, subscription)) return false;
  const subOk =
    !!subscription &&
    ['active', 'trialing', 'past_due'].includes(
      subscription.status as 'active' | 'trialing' | 'past_due'
    );
  const tier = company.plan_tier;
  const paidTier =
    !!tier &&
    tier !== 'free' &&
    tier !== 'enterprise' &&
    company.enterprise !== true;
  return subOk && paidTier;
}

/**
 * Tabs shown on Billing / Pricing for V2: subscribed call → call+bundle; chat → chat+bundle;
 * bundle → all three (compare call/chat lines alongside bundle). Everyone else sees all three.
 */
export function getVisibleV2PlanTabs(
  company: Company | null | undefined,
  subscription: Subscription | null | undefined
): V2PlanTab[] {
  const all: V2PlanTab[] = ['call', 'chat', 'bundle'];
  if (isEnterpriseSubscriber(company, subscription)) {
    return all;
  }
  if (!isPaidPlanSubscriber(company, subscription) || !company?.plan_type) {
    return all;
  }
  if (company.plan_type === 'call') return ['call', 'bundle'];
  if (company.plan_type === 'chat') return ['chat', 'bundle'];
  if (company.plan_type === 'bundle') return all;
  return all;
}

/**
 * Paid single-line plan: hide the agent list for the product line not on the
 * Free tier and enterprise keep both lists; limits apply via
 * shouldBlockNewCallAgent / shouldBlockNewChatAgent in pricingAgentLimits.
 */
export function getSingleLinePaidPlanAgentsVisibility(
  company: Company | null | undefined,
  subscription: Subscription | null | undefined
): { hideCallAgentsList: boolean; hideChatAgentsList: boolean } {
  if (!company) {
    return { hideCallAgentsList: false, hideChatAgentsList: false };
  }
  if (!isPaidPlanSubscriber(company, subscription)) {
    return { hideCallAgentsList: false, hideChatAgentsList: false };
  }
  return {
    hideCallAgentsList: company.plan_type === 'chat',
    hideChatAgentsList: company.plan_type === 'call',
  };
}
